import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../context/StoreContext";
import { api, fmtPrice } from "../lib/api";

export const Logo = () => (
  <Link to="/" className="logo" data-testid="nav-logo"><div className="cube"></div>UNREAL<em>ACADEMY</em></Link>
);

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { cart, bundleCart, wishlist, sections, currency, changeCurrency } = useStore();
  const [q, setQ] = useState("");
  const [sug, setSug] = useState([]);
  const [menu, setMenu] = useState(false);
  const nav = useNavigate();
  const box = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setSug(null); return; }
    const t = setTimeout(() => api.get(`/search/suggest?q=${encodeURIComponent(q.trim())}`).then((r) => setSug(r.data)).catch(() => {}), 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => {
      if (box.current && !box.current.contains(e.target)) setSug(null);
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);

  const goResults = (params) => { setSug(null); setQ(""); nav(`/courses?${new URLSearchParams(params)}`); };
  const secMap = Object.fromEntries(sections.map((s) => [s.id, s]));

  return (
    <nav className="forge-nav">
      <Logo />
      <div id="navLinks">
        {sections.filter((s) => s.enabled !== false && s.counts?.total > 0).slice(0, 4).map((s) => (
          <div className="nav-item" key={s.id}>
            <button className="nav-a" data-testid={`nav-section-${s.id}`} onClick={() => goResults({ section: s.id })}>{s.title} <span className="car">▾</span></button>
            <div className="nav-drop">
              <button onClick={() => goResults({ section: s.id })}>{s.icon} All {s.title}<span>{s.counts?.total ?? 0}</span></button>
              <button onClick={() => goResults({ section: s.id, level: "Beginner" })}><i className="dotb"></i> Beginner<span>{s.counts?.Beginner ?? 0}</span></button>
              <button onClick={() => goResults({ section: s.id, level: "Beginner–Intermediate" })}><i className="dotbi"></i> Beginner–Intermediate<span>{s.counts?.["Beginner–Intermediate"] ?? 0}</span></button>
              <button onClick={() => goResults({ section: s.id, level: "Intermediate" })}><i className="doti"></i> Intermediate<span>{s.counts?.Intermediate ?? 0}</span></button>
              <button onClick={() => goResults({ section: s.id, level: "Advanced" })}><i className="dota"></i> Advanced<span>{s.counts?.Advanced ?? 0}</span></button>
            </div>
          </div>
        ))}
      </div>
      <div className="nav-search" ref={box}>
        <div className="box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
          <input data-testid="nav-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") goResults({ q: q.trim() }); if (e.key === "Escape") setSug(null); }}
            placeholder="Search for anything… Blueprints, Landscapes, AI" autoComplete="off" />
        </div>
        {sug && sug.length > 0 && (
          <div className="suggest" data-testid="search-suggestions">
            {sug.map((s) => (
              <button key={s.id} className="sg" onClick={() => { setSug(null); setQ(""); nav(`/course/${s.id}`); }}>
                <div className="ic">{secMap[s.section]?.icon || "🎓"}</div>
                <div><b>{s.title}</b><span>{secMap[s.section]?.title || s.section} · {s.level} · {(s.sale_price ?? s.price) === 0 ? "Free" : "$" + (s.sale_price ?? s.price)}</span></div>
              </button>
            ))}
            <button className="sg" onClick={() => goResults({ q: q.trim() })}>
              <div className="ic">🔍</div><div><b>See all results for “{q.trim()}”</b></div>
            </button>
          </div>
        )}
      </div>
      <div className="nav-actions">
        <select className="cur-sel" data-testid="currency-select" value={currency} onChange={(e) => changeCurrency(e.target.value)}>
          <option value="USD">USD</option><option value="EUR">EUR</option>
        </select>
        <button className="icon-btn" data-testid="nav-wishlist" title="My List" onClick={() => nav("/wishlist")}>
          ♡{wishlist.length > 0 && <span className="badge">{wishlist.length}</span>}
        </button>
        <button className="icon-btn" data-testid="nav-cart" title="Cart" onClick={() => nav("/cart")}>
          🛒{cart.length + bundleCart.length > 0 && <span className="badge" data-testid="cart-count">{cart.length + bundleCart.length}</span>}
        </button>
        {user ? (
          <div style={{ position: "relative" }} ref={menuRef}>
            <button className="btn-ghost" data-testid="nav-user-menu" onClick={() => setMenu(!menu)}>
              {user.name.split(" ")[0]} ▾
            </button>
            {menu && (
              <div className="user-drop">
                <Link data-testid="menu-my-learning" to="/my-learning" onClick={() => setMenu(false)}>🎓 My Learning</Link>
                <Link data-testid="menu-account" to="/account" onClick={() => setMenu(false)}>👤 Account</Link>
                {user.role === "admin" && <Link data-testid="menu-admin" to="/admin" onClick={() => setMenu(false)} style={{ color: "var(--gold)" }}>⚙ Admin Panel</Link>}
                <button data-testid="menu-logout" onClick={() => { setMenu(false); logout(); }} style={{ color: "var(--ember)" }}>⏻ Log out</button>
              </div>
            )}
          </div>
        ) : (
          <button className="btn-ghost" data-testid="nav-login" onClick={() => nav("/auth")}>Sign In</button>
        )}
        <button className="btn-primary" data-testid="nav-start-learning" onClick={() => nav("/courses")}>Start Learning</button>
      </div>
    </nav>
  );
};

export const Footer = () => (
  <footer>
    <Logo />
    <p>The Unreal Engine academy for builders — Blueprints, Landscapes, and beyond.</p>
    <div className="foot-links">
      <Link to="/instructor">Instructor</Link>
      <Link to="/terms">Terms</Link>
      <Link to="/privacy">Privacy</Link>
      <Link to="/refund-policy">Refund Policy</Link>
      <Link to="/impressum">Impressum</Link>
    </div>
    <div className="secure-pay" data-testid="secure-payments">
      <span className="lock">🔒</span> Secure checkout — payments processed by <b>Stripe</b> &amp; <b>PayPal</b> · SSL encrypted · We never store your card details
    </div>
    <p>© 2026 Unreal Academy. All rights reserved. Not affiliated with Epic Games — "Unreal Engine" is a trademark of Epic Games, Inc.</p>
  </footer>
);
